import React, { createContext, useContext, useEffect, useState } from 'react';
import Auth from '../services/auth';
import jwt from 'jwt-decode';

export const AccessTokenKey = 'access-token';
export const RefreshTokenKey = 'refresh-token';

export enum Role {
  None,
  Lecteur,
  Abonne,
  AbonneDecouverte,
  AbonneEtudiant,
  AbonneClassique,
  AbonnePremium,
  AbonneLegacy,
  Administrateur,
  Chroniqueur,
  Communaute,
  Editorial1,
  Contributeur,
  Editorial2,
  Redacteur,
  RedacteurChef,
  RedacteurJunior
}

export interface JWT {
  Email: string
  Role: string[]
  Sub: string
  Typ: string
  iat: Date
  exp: Date
  iss: string
  nbf: Date
}

export interface AuthenticationProps {
  isLogged: boolean
  role: Role[]
  email: string
  login: (email: string, password: string) => Promise<boolean>
  logout: () => void
  refresh: () => Promise<boolean>
}

const authenticationDefault: AuthenticationProps = {
  isLogged: false,
  role: [],
  email: '',
  login: () => Promise.resolve(false),
  logout: () => { },
  refresh: () => Promise.resolve(false),
}

export const AuthenticationContext = createContext<AuthenticationProps>(authenticationDefault);

const parseRole = (role: string): Role => {
  const roleId: number = Object.values(Role).findIndex(r => r === role);

  if (roleId < 0)
    return Role.None;

  return roleId;
}

export const AuthenticationContextProvider = ({ children }: any) => {
  const [logged, setLogged] = useState(localStorage.getItem(AccessTokenKey) !== null);
  const [role, setRole] = useState<Role[]>([]);
  const [email, setEmail] = useState<string>('');

  const checkLogged = () => {
    const accessToken = localStorage.getItem(AccessTokenKey);

    setLogged(accessToken !== null);

    if (accessToken) {
      const parsed: any = jwt(accessToken);
      const roles = parsed.roles as string[];

      const r: Role[] = roles.map(parsedRole => parseRole(parsedRole));
      setRole(r);
      setEmail(parsed.Email);
    }
  }

  useEffect(() => {
    checkLogged();
    window.addEventListener('storage', checkLogged);

    return () => {
      window.removeEventListener('storage', checkLogged);
    }
  }, []);

  const login = (email: string, password: string) => {
    return Auth.login(email, password)
      .then((res) => {
        checkLogged();
        return res;
      });
  };

  const logout = () => {
    Auth.logout();
    checkLogged();
  };

  const refresh = Auth.refresh;

  return (
    <AuthenticationContext.Provider value={{ login, logout, refresh, isLogged: logged, role, email }}>
      {children}
    </AuthenticationContext.Provider>
  );
}

export const useAuthentication = () => useContext(AuthenticationContext);